import React, { useEffect, useMemo, useState } from "react";
import ContactSection from "../components/ContactSection";
import { fetchMenu } from "../lib/api";

const categories = [
  "All",
  "Breakfast",
  "Soups",
  "Starters",
  "Mains",
  "Sides",
  "Beverages",
];

export default function MenusPage() {
  const [active, setActive] = useState("All");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchMenu()
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || "Failed to load menu");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const list = useMemo(
    () => (active === "All" ? items : items.filter((m) => m.category === active)),
    [items, active]
  );

  return (
    <div className="fade-in">
      <section className="bg-[var(--ka-cream)]">
        <div className="container-ka pt-16 pb-10 text-center">
          <span className="inline-block text-[12px] tracking-[0.25em] uppercase text-[var(--ka-teal)] mb-3">
            Our Menu
          </span>
          <h1 className="font-serif text-5xl md:text-6xl text-[var(--ka-teal-darker)] mb-4">
            Fresh, home-made, Keto-perfect
          </h1>
          <p className="text-[var(--ka-muted)] max-w-2xl mx-auto">
            A rotating selection of our most-loved meals. Every dish is
            macro-counted and cooked the morning of delivery.
          </p>
        </div>
      </section>

      <section className="bg-[var(--ka-cream)]">
        <div className="container-ka pb-16">
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`px-5 py-2 text-sm rounded-full border transition-colors ${
                  active === c
                    ? "bg-[var(--ka-teal)] text-white border-[var(--ka-teal)]"
                    : "bg-white text-[var(--ka-text)] border-gray-200 hover:border-[var(--ka-teal)] hover:text-[var(--ka-teal)]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {error && (
            <div className="text-center text-red-600 py-6">
              {error} - please refresh the page.
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading &&
              Array.from({ length: 9 }).map((_, i) => (
                <div
                  key={i}
                  className="menu-card overflow-hidden rounded-md animate-pulse"
                >
                  <div className="aspect-[4/3] bg-gray-200" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 w-20 bg-gray-200 rounded" />
                    <div className="h-5 w-3/4 bg-gray-200 rounded" />
                    <div className="h-4 w-full bg-gray-200 rounded" />
                  </div>
                </div>
              ))}
            {!loading &&
              list.map((m) => (
                <article
                  key={m.id}
                  className="menu-card overflow-hidden rounded-md"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={m.image}
                      alt={m.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] tracking-[0.18em] uppercase text-[var(--ka-teal)]">
                        {m.category}
                      </span>
                      <span className="text-sm font-medium">
                        &#8377;{m.price}
                      </span>
                    </div>
                    <h3 className="font-serif text-2xl text-[var(--ka-teal-darker)] mb-1">
                      {m.name}
                    </h3>
                    <p className="text-sm text-[var(--ka-muted)] mb-3">
                      {m.description}
                    </p>
                    <div className="flex gap-3 text-[11px] text-[var(--ka-muted)] border-t pt-3">
                      <span>{m.macros.kcal} kcal</span>
                      <span>F {m.macros.fat}g</span>
                      <span>P {m.macros.protein}g</span>
                      <span>NC {m.macros.net_carbs}g</span>
                    </div>
                  </div>
                </article>
              ))}
          </div>
          {!loading && list.length === 0 && !error && (
            <div className="text-center text-[var(--ka-muted)] py-12">
              No items in this category yet.
            </div>
          )}
        </div>
      </section>

      <ContactSection />
    </div>
  );
}
